import { QIcon } from '../../QIcons';
import {
  QButton,
  QButtonGroup,
  QDivider,
  QIconButton,
  QText,
} from '../../QAtoms';
import { QStack } from '../../QLayouts';
import { FileListItem, QFileInputValue } from './types';
import { type FC, type ReactElement, useMemo, useState } from 'react';
import { QDeleteConfirmationModal } from '..';
import { useLibraryTranslation } from '../../i18n';

const FileItem: FC<
  {
    file: FileListItem;
    requireConfirmation?: boolean;
  } & Pick<FileListProps, 'onDelete' | 'onDownload'>
> = ({ file, onDelete, onDownload, requireConfirmation = true }) => {
  const { t } = useLibraryTranslation();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleDeleteClick = () => {
    if (file.errorMessage || !requireConfirmation) {
      // Delete immediately if there's an error or confirmation is not required
      onDelete(file);
    } else {
      // Show confirmation modal
      setShowConfirmation(true);
    }
  };

  return (
    <>
      <QStack
        alignItems="center"
        direction="row"
        justify="space-between"
        data-cy={`attachment-record-${file.id}`}
      >
        <QStack direction="row">
          <QText fontSize="sm" fontWeight="semibold">
            {file.filename}
          </QText>
          {file.errorMessage && (
            <QStack direction="row" alignItems="center">
              <QIcon iconName="FillXCircleFill" color="red.500" />
              <QText fontSize="sm" color="red.500">
                {file.errorMessage}
              </QText>
            </QStack>
          )}
        </QStack>
        <QButtonGroup>
          {onDownload && (
            <QIconButton
              iconName="Download"
              data-cy={`attachment-download-${file.id}`}
              aria-label={t('fileInput.downloadAttachment')}
              onClick={() => onDownload(file)}
            />
          )}
          <QButton
            width={8}
            aria-label="file-remove-button"
            data-cy={`attachment-remove-${file.id}`}
            variant="ghost"
            isDestructive
            onClick={handleDeleteClick}
          >
            <QIcon iconName="Trash" />
          </QButton>
        </QButtonGroup>
      </QStack>
      {showConfirmation && (
        <QDeleteConfirmationModal
          onConfirm={() => onDelete(file)}
          onClose={() => setShowConfirmation(false)}
          title={t('fileInput.deleteFileTitle')}
          message={<QText>{t('fileInput.deleteFileConfirm')}</QText>}
        />
      )}
    </>
  );
};

type FileListProps = {
  onDelete: (value: QFileInputValue) => void;
  onDownload?: (value: QFileInputValue) => void;
  files: readonly FileListItem[];
  requireConfirmation?: boolean;
};

export const FileItemList: FC<FileListProps> = ({
  onDelete,
  onDownload,
  files,
  requireConfirmation = true,
}) => {
  const joinedComponents = useMemo(() => {
    return files.reduce(
      (
        acc: readonly ReactElement[],
        current: QFileInputValue,
        index: number,
      ) => {
        if (index === 0) {
          return [
            <FileItem
              file={current}
              onDelete={onDelete}
              onDownload={onDownload}
              requireConfirmation={requireConfirmation}
              key={current.id}
            />,
          ];
        }

        return [
          ...acc,
          <QDivider key={`${current.id}-divider`} />,
          <FileItem
            file={current}
            onDelete={onDelete}
            onDownload={onDownload}
            requireConfirmation={requireConfirmation}
            key={current.id}
          />,
        ];
      },
      [],
    );
  }, [files, onDelete, onDownload, requireConfirmation]);

  return (
    <QStack
      border="1px"
      borderRadius="4px"
      padding="16px"
      borderColor="gray.200"
      spacing={4}
    >
      {joinedComponents}
    </QStack>
  );
};
